/*
 * <collection-tabs> — O'Neill Canada tabbed collection browser.
 * Wraps the collection-list section's rendered content. Each `.collection-tabs__panel`
 * (rendered by a `collection-tab` block) becomes a tab; this element builds the tab
 * bar from the panels' `data-tab-title`, shows the first panel, and handles
 * switching (mouse + keyboard). With JS off, all panels stay visible and shoppable.
 *
 * setup() is idempotent: the theme editor re-runs connectedCallback on every block
 * move/reorder, so we always tear down any previously-built bar first (otherwise the
 * tab bar duplicates each time you move something).
 */
class CollectionTabs extends HTMLElement {
  connectedCallback() {
    this.setup();
  }

  setup() {
    this.wireCarousels();

    // Idempotency: remove any bar we injected on a previous run before rebuilding.
    this.querySelectorAll('.collection-tabs__bar').forEach((bar) => bar.remove());

    this.panels = Array.from(this.querySelectorAll('.collection-tabs__panel'));

    // Nothing to tab with 0 or 1 panel — restore visibility and bail.
    if (this.panels.length < 2) {
      delete this.dataset.enhanced;
      this.panels.forEach((panel) => {
        panel.hidden = false;
        panel.classList.remove('is-entering');
      });
      return;
    }

    this.dataset.enhanced = 'true';

    const bar = document.createElement('div');
    bar.className = 'collection-tabs__bar';
    bar.setAttribute('role', 'tablist');

    this.tabs = this.panels.map((panel, i) => {
      const id = panel.id || `ct-panel-${i}`;
      panel.id = id;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `${id}-tab`);
      panel.setAttribute('tabindex', '0');

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'collection-tabs__tab';
      tab.id = `${id}-tab`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      tab.textContent = panel.getAttribute('data-tab-title') || `Tab ${i + 1}`;
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
      bar.appendChild(tab);
      return tab;
    });

    // Insert the bar right before the first panel (under any header block, above panels).
    this.panels[0].parentNode.insertBefore(bar, this.panels[0]);
    this.select(0, false);
  }

  select(index, focusTab = true) {
    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.hidden = !active;
      panel.classList.toggle('is-entering', active);
      this.tabs[i].setAttribute('aria-selected', active ? 'true' : 'false');
      this.tabs[i].tabIndex = active ? 0 : -1;
    });
    if (focusTab) this.tabs[index].focus();
    this.activeIndex = index;
  }

  wireCarousels() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    this.querySelectorAll('.collection-tabs__carousel-wrap').forEach((wrap) => {
      // Idempotent: don't re-bind arrows on theme-editor re-renders.
      if (wrap.dataset.wired === 'true') return;
      const track = wrap.querySelector('[data-carousel]');
      if (!track) return;
      wrap.dataset.wired = 'true';
      const step = () => Math.max(track.clientWidth * 0.9, 240);
      const prev = wrap.querySelector('[data-carousel-prev]');
      const next = wrap.querySelector('[data-carousel-next]');
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
    });
  }

  onKeydown(event, i) {
    const count = this.tabs.length;
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (i + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (i - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(next);
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
